import { api } from "jiffy-api";

import "common/jiffy/cable";
import { findIndexById } from "common/utils/helpers/find-index-by-id";
import {
  updateHTML,
  setPDPDatesLoading
} from "components/jiffy/product-details-delivery-date-simple/init-dates";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { trackRudderstackImageProcessingCompleted } from "common/utils/rudderstack/tracker";
import { BUSINESS_LINE } from "common/utils/app-registry/contants";
import { DTF_PROGRESS_MAPPING } from "common/utils/progress-bar/constants";
import { DEFAULT_STICKER_VALUES } from "common/utils/stickers/constants";
import prometheusTracker from "common/utils/prometheus-tracker";

import ErrorTypes from "./error-types";
// eslint-disable-next-line import/no-cycle
import { SPANGLE_MODES, useSpanglesStore } from "./spangles-module";
import { useStickersStore } from "./stickers-module";
import { getRandomId, recalculateCustomSizes } from "./utils";

const BLACK_STICKER_BACKGROUND_THRESHOLD = 50;
const IMAGE_UPLOAD_ENHANCEMENTS_STUCK_THRESHOLD = 60;

const isTrackedAction = action => DTF_PROGRESS_MAPPING[action] !== undefined;

const setProgress = (data, store) => {
  const progressIndex = store.isSpangle ? 1 : 0;
  const { label, percentage } = DTF_PROGRESS_MAPPING[data.action];
  const progressLabel = label[progressIndex] || label[0];
  const progressPercentage = percentage[progressIndex] || percentage[0];

  store.updateArtworkValuesById({
    id: data.result?.artwork_id || data.data?.artwork_id,
    progress: progressPercentage,
    progressMessage: progressLabel
  });
};

const setLastEvent = (data, store) => {
  store.updateArtworkValuesById({
    id: data.result?.artwork_id || data.data?.artwork_id,
    lastWebSocketEvent: data.action,
    lastWebSocketEventData: {
      result: data.result?.process_data || data.data,
      data: data.result?.data || data.data
    }
  });
};

const handleValidateArtworkProgress = (artworkId, store) => {
  store.setValidateArtworkStartTime(artworkId, Date.now());
  const timerInterval = setInterval(() => {
    const timeElapsed = Math.floor((Date.now() - store.validateArtworkStartTime[artworkId]) / 1000);
    if (timeElapsed > IMAGE_UPLOAD_ENHANCEMENTS_STUCK_THRESHOLD) {
      store.clearValidateArtworkStartEventInterval(artworkId);

      submitDtfEvent("image_upload_enhancements_stuck", {
        dtf_channel_uuid: store.dtfChannelUuid,
        artwork_id: artworkId
      });
    }
  }, 1000);

  store.setValidateArtworkStartEventIntervalID(artworkId, timerInterval);
};

const getArtwork = (artworkData, store) => {
  const { result } = artworkData;

  return store.artworks.find(artwork => {
    const artworkId = typeof artwork.id === "string" ? artwork.id.match(/^\d+/)[0] : artwork.id;
    return (
      Number(artworkId) === Number(result?.artwork_id) ||
      artwork.originalIndex?.toString() === result?.data?.file_original_index?.toString()
    );
  });
};

/* eslint-disable max-lines, max-lines-per-function */
export default store => {
  const spangleStore = useSpanglesStore();
  const stickerStore = useStickersStore();
  const params = { uuid: store.dtfChannelUuid, channel: "DtfChannel" };
  const { App } = window;

  /* eslint-disable-next-line no-undef */
  const cable = App.cable.subscriptions.create(params, {
    received(/** @type {import('jiffy-api').models.cable.DtfChannel.Actions} */ data) {
      if (!store.actionCableConnected) {
        store.setActionCableConnected(true);
      }

      const { result } = data;
      const currentArtwork = getArtwork(data, store);
      const timeStampMismatch =
        !!result?.data?.process_time_stamp &&
        String(currentArtwork?.processTimeStamp) !== String(result.data?.process_time_stamp);

      if (timeStampMismatch) return;

      if (isTrackedAction(data.action)) {
        store.setIsRetrying(false);
        setProgress(data, store);
        setLastEvent(data, store);
      }

      if (store.isSpangle) {
        store.artworks[0].spangled = true;
      }

      const eventName =
        data.action === "process_dtf_artwork_started"
          ? "image_upload_enhancements_started"
          : `image_upload_${data.action}_triggered`;
      submitDtfEvent(eventName, {
        payload: {
          process_data: result,
          stream_name: `dtf_channel_${store.dtfChannelUuid}`,
          action_type: data.action,
          worker_enhancement: data.worker_enhancement
        },
        business_line: store.businessLine,
        artwork_id: result.artwork_id,
        file_type: result?.file_type
      });

      if (data.action === "spangle") {
        let spangleData;

        if (store.isSpangle && spangleStore.spangleVariations.length === 0) {
          spangleStore.prepareVariations({ isRetry: false });
        }

        if (result.success) {
          submitDtfEvent("spangle_api_call_completed", {
            algorithm: result.algorithm,
            duration: result.duration / 1000,
            artwork_url: result.original_artwork_url,
            pages_info: result.pages_info,
            business_line: BUSINESS_LINE.SPANGLE
          });

          spangleData = {
            algorithm: result.algorithm,
            imageUrl: result.design_url,
            spangleCsv: result.spangle_csv,
            spanglePagesInfo: result.pages_info,
            positionsJson: result.positions_json,
            originalImageUrl: result.original_image_url,
            error: false
          };

          spangleStore.setOriginalArtworkUrl(result.original_image_url);
        } else {
          spangleData = { error: true, algorithm: result.algorithm, pages_info: result.pages_info };
          window.Honeybadger?.notify("DTF spangle variations error", {
            params: {
              error_message: result.error,
              algorithm: result.algorithm,
              pages_info: result.pages_info
            }
          });
        }

        // There are times that reset in `removeArtwork` doesn't succeed
        // Adding this reset ensures that completedSpangleData is empty before
        // we proceed.
        if (spangleStore.completedSpangleData.length === result.algorithms_size) {
          spangleStore.resetCompletedSpangleData();
        }

        spangleStore.addCompletedSpangleData(spangleData);

        if (spangleStore.completedSpangleData.length === result.algorithms_size) {
          spangleStore.showSpangleResult();
          spangleStore.setSpangleMode(SPANGLE_MODES.sizeEdited);
        }

        return;
      }

      if (data.action === "spangle_update") {
        store.updateArtworkValues({
          index: 0,
          spangledImageUrl: result.design_url
        });

        return;
      }

      if (data.action === "upload_artwork") {
        this.perform("process_dtf_artwork", { ...data.data, experiments: store.experiments });
        return;
      }

      if (data.action === "validate_artwork") {
        if (result.success) {
          handleValidateArtworkProgress(currentArtwork.id, store);
          return;
        }

        store.updatePrintReadyInfoState(result.artwork_id);

        submitDtfEvent("image_processing_completed", {
          payload: {
            process_data: result,
            stream_name: `dtf_channel_${store.dtfChannelUuid}`,
            action_type: data.action,
            worker_enhancement: data.worker_enhancement
          },
          business_line: store.businessLine,
          artwork_id: result.artwork_id
        });

        trackRudderstackImageProcessingCompleted({
          artwork_id: result.artwork_id,
          dpi: result.artwork?.metadata?.dpi,
          location: result.artwork?.metadata?.uploaded_on,
          file_name: result.artwork?.filename,
          file_type: result.artwork?.filetype,
          file_size: result.artwork?.filesize,
          artworks_count: store.artworks.length,
          business_line: store.businessLine,
          pre_cut_transfers: store.preCut,
          enhanced_image_url: result.artwork?.no_bg_imgix_url || result.artwork?.with_bg_imgix_url,
          original_image_url: result.artwork?.original_image_imgix_url
        });
      }

      if (data.action === "process_artwork") {
        store.clearValidateArtworkStartEventInterval(currentArtwork.id);
        if (result.success) {
          prometheusTracker.trackImageProcessed(result?.process_data?.original_mime);

          return;
        }

        store.getArtworksPricing();
        if (result.artwork?.metadata?.errors?.length > 0) {
          store.setGangsheetValidationError({
            index: findIndexById(store.artworks, result.artwork_id),
            artwork: result.artwork,
            type: ErrorTypes.INVALID_GANG_SHEET
          });
        }
      }

      if (data.action === "extract_bg_metadata") {
        if (result.success) {
          return;
        }
      }

      if (data.action === "spangle_algorithm_list") {
        if (result.success) {
          return;
        }
      }

      if (data.action === "fetch_price") {
        if (result.success) {
          let { response } = result;

          setPDPDatesLoading(true);

          if (store.showMultiSize) {
            const artworks = {};

            Object.keys(response.artworks).forEach(key => {
              artworks[key] = [response.artworks[key]];
            });

            response = { ...response, artworks };
          }

          store.setArtworkPricing({
            response,
            isMultiSize: store.showMultiSize
          });

          return;
        }
      }

      if (data.action === "fetch_delivery_date") {
        if (result.success) {
          setPDPDatesLoading(false);
        }

        if (!currentArtwork) return;

        const { imageProcessingCompletedEventSent } = currentArtwork;

        updateHTML(result.result);

        if (store.isSpangle) {
          spangleStore.setOriginalArtworkUrl(result.artwork.url);
        } else if (store.isGoldFoil) {
          store.convertImageToHexColor({ artwork: result.artwork });
        } else {
          store.setLoadedArtwork({ artwork: result.artwork });

          const whitePixelsRatio = Number(result.artwork.metadata?.white_pixels_ratio || 0);
          store.setPresetMockupColor({
            index: findIndexById(store.artworks, result.artwork_id),
            color: whitePixelsRatio > 50 ? "#000000" : "#E1E1E1"
          });

          if (store.isSticker || store.isStickerSample) {
            store.artworks.forEach((artwork, artworkIndex) => {
              if (artwork.id) {
                // const { id, stickerCutlineNoBgUrl, stickerCutlineWithBgUrl, metadata } = artwork;
                const shape = artwork.shape || DEFAULT_STICKER_VALUES.shape;
                const paddingSize =
                  artwork.paddingSize === "no_padding"
                    ? DEFAULT_STICKER_VALUES.paddingSize
                    : artwork.paddingSize;
                const pixelRatio = Number(artwork.metadata?.whitePixelsRatio || 0);
                const bgColor =
                  pixelRatio > BLACK_STICKER_BACKGROUND_THRESHOLD
                    ? "#000000"
                    : DEFAULT_STICKER_VALUES.bgColor;

                // store.dispatch("stickers/updateArtworkDataCache", {
                //   id,
                //   shape,
                //   bgColor,
                //   paddingSize,
                //   data: { stickerCutlineNoBgUrl, stickerCutlineWithBgUrl, metadata }
                // });
                store.updateArtworkValuesById({
                  id: artwork.id,
                  shape,
                  bgColor,
                  paddingSize
                });

                // Reload artwork after updateArtworkValuesById
                const updatedArtwork = store.artworks[artworkIndex];

                stickerStore.generateStickerImage({
                  artwork: updatedArtwork,
                  shape,
                  bgColor,
                  paddingSize
                });
              }
            });
          }
        }

        if (store.isStandardArtwork) {
          const { showMultiSize, artworks, sticker, initialValues } = store;
          const artwork = artworks.find(item => item.id === currentArtwork.id);
          const metadata = data.result.artwork?.metadata;
          let sizesAndQuantities = [];

          if (showMultiSize) {
            const enhancedMetadata = data.result.artwork?.enhanced_metadata ?? metadata;
            sizesAndQuantities = [
              {
                width: enhancedMetadata?.width_inch,
                height: enhancedMetadata?.height_inch,
                quantity: initialValues.quantity,
                preset: "custom",
                id: getRandomId()
              }
            ];

            if (artwork.isSizesEdited) {
              sizesAndQuantities = recalculateCustomSizes(
                artwork.sizesAndQuantities,
                metadata.aspect_ratio,
                sticker
              );
            }
          }

          store.updateArtworkValuesById({
            id: currentArtwork.id,
            originalImageUrl: data.result.artwork?.original_image_url,
            enhancedWithBgUrl: data.result.artwork?.with_bg_url,
            enhancedNoBgUrl: data.result.artwork?.no_bg_url,
            enhancedDpi: data.result.artwork?.metadata?.dpi,
            enhancedOriginalDpi: data.result.artwork?.original_dpi,
            originalDpi: 300,
            sliderPreviewUrl: data.result.artwork?.no_bg_no_trim_url,
            originalMime: data.result.artwork?.original_mime,
            enhancementCompleted: true,
            sizesAndQuantities
          });

          if (store.isGoldFoil) {
            store.generateGoldFoilImages({ artworkId: currentArtwork.id });
          }

          submitDtfEvent("dtf_image_print_perfect_toggle", {
            business_line: store.businessLine,
            artwork_id: currentArtwork.id,
            enhance_image: true,
            selected_by: "jiffy",
            metadata: data.result.artwork?.metadata
          });
        }

        if (store.artworks.length >= 1 && !store.artworks.some(artwork => artwork.isLoading)) {
          store.getArtworksPricing();
        }

        if (!imageProcessingCompletedEventSent) {
          submitDtfEvent("image_processing_completed", {
            payload: {
              process_data: result,
              stream_name: `dtf_channel_${store.dtfChannelUuid}`,
              action_type: data.action,
              worker_enhancement: data.worker_enhancement
            },
            business_line: store.businessLine,
            artwork_id: result.artwork_id
          });

          // Handle both Full Enhancement (*_imgix_url) and Lightweight Pipeline (*_url) field names
          const enhancedImageUrl =
            result.artwork?.no_bg_imgix_url ||
            result.artwork?.with_bg_imgix_url ||
            result.artwork?.no_bg_url ||
            result.artwork?.with_bg_url;
          const originalImageUrl =
            result.artwork?.original_image_imgix_url || result.artwork?.original_image_url;

          trackRudderstackImageProcessingCompleted({
            artwork_id: result.artwork_id,
            dpi: result.artwork?.metadata?.dpi,
            location: result.artwork?.metadata?.uploaded_on,
            file_name: result.artwork?.filename,
            file_type: result.artwork?.filetype,
            file_size: result.artwork?.filesize,
            business_line: store.businessLine,
            artworks_count: store.artworks.length,
            pre_cut_transfers: store.preCut,
            ...(enhancedImageUrl && { enhanced_image_url: enhancedImageUrl }),
            ...(originalImageUrl && { original_image_url: originalImageUrl })
          });

          api.dtf.acknowledgeArtwork({ id: result.artwork_id }).catch(() => {
            // Silently ignore errors
          });

          store.updateArtworkValuesById({
            id: result.artwork_id,
            imageProcessingCompletedEventSent: true
          });

          store.updatePrintReadyInfoState(result.artwork_id);
        }

        return;
      }

      if (data.action === "spangle_image") {
        spangleStore.setLastSpangledDimensions({
          width: result.artwork.metadata.width_inch,
          height: result.artwork.metadata.height_inch
        });
        store.setLoadedArtwork({ artwork: result.artwork });
        spangleStore.setOriginalArtworkUrl(result.artwork.url);

        return;
      }

      if (!result.success) {
        const { artwork } = result;

        store.handleProcessError({
          file: {
            originalIndex: findIndexById(store.artworks, result.artwork_id),
            name: artwork?.name,
            type: artwork?.filetype,
            size: artwork?.filesize,
            width: artwork?.metadata?.width,
            height: artwork?.metadata?.height
          },
          errorResponse: result
        });
      }
    },
    connected() {
      const {
        artworkIds,
        artworkWidth,
        artworkHeight,
        processArtwork,
        urlPreCut,
        fileContentType,
        artworkPaddingSize,
        artworkBgColor,
        artworkColor,
        artworkShape
      } = store.actionCableOptions;

      if (artworkIds) {
        store.preloadArtworks({
          artworkIds,
          artworkWidth,
          artworkHeight,
          processArtwork,
          urlPreCut,
          fileContentType,
          artworkPaddingSize,
          artworkBgColor,
          artworkColor,
          artworkShape
        });
      }

      if (!store.actionCableConnected) {
        store.setActionCableConnected(true);
        store.setIsRetrying(true);

        if (store.isSpangle) {
          spangleStore.resetCompletedSpangleData();
        }

        store.artworks.forEach(artwork => {
          if (
            artwork.lastWebSocketEvent &&
            Object.keys(artwork.lastWebSocketEventData).length > 0
          ) {
            // Reset artwork processTimeStamp
            // There will be cases where discontinued continue sending messages.
            // Rejecting it to prioritize messages coming from this process since
            // this is the source of truth.
            const processTimeStamp = Date.now();

            this.perform("continue_dtf_artwork_process", {
              last_process: artwork.lastWebSocketEvent,
              last_process_data: artwork.lastWebSocketEventData.result,
              data: {
                ...artwork.lastWebSocketEventData.data,
                processTimeStamp,
                experiments: store.experiments
              }
            });
            store.updateArtworkValuesById({
              id: artwork.id,
              lastWebSocketEvent: null,
              lastWebSocketEventData: {},
              processTimeStamp
            });
          }
        });
      }
    },
    disconnected() {
      store.setActionCableConnected(false);
    },
    spangle(data = {}) {
      this.perform("spangle", data);
    }
  });

  return cable;
};
/* eslint-enable max-lines, max-lines-per-function */
